import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import {
  setActiveCharacter,
  selectActiveCharacterId,
  selectCharacterById,
  setCharacters,
  setFamilies,
} from './charactersSlice';
import CharactersListPage from './CharactersListPage';
import CharacterDetailsPanel from './CharacterDetailsPanel';
import AddCharacterModal from './AddCharacterModal';
import FamilyTreeModal from './FamilyTreeModal';
import useThemeStyles from '../../hooks/useThemeStyles';
import characterService from '../../utils/apiRequests/characterService';

/**
 * Normalize character data from backend to frontend format
 */
const normalizeCharacter = (backendCharacter) => {
  if (!backendCharacter) return null;

  const normalized = {
    ...backendCharacter,
    id: backendCharacter.id || backendCharacter._id?.toString() || backendCharacter._id,
    projectId: backendCharacter.projectId?.toString() || backendCharacter.projectId,
    familyId: backendCharacter.familyId?.toString() || backendCharacter.familyId || null,
  };

  // Convert Date strings to epoch timestamps
  if (backendCharacter.createdAt) {
    if (typeof backendCharacter.createdAt === 'string') {
      normalized.createdAt = new Date(backendCharacter.createdAt).getTime();
    } else if (backendCharacter.createdAt instanceof Date) {
      normalized.createdAt = backendCharacter.createdAt.getTime();
    } else {
      normalized.createdAt = backendCharacter.createdAt;
    }
  } else {
    normalized.createdAt = Date.now();
  }

  if (backendCharacter.updatedAt) {
    if (typeof backendCharacter.updatedAt === 'string') {
      normalized.updatedAt = new Date(backendCharacter.updatedAt).getTime();
    } else if (backendCharacter.updatedAt instanceof Date) {
      normalized.updatedAt = backendCharacter.updatedAt.getTime();
    } else {
      normalized.updatedAt = backendCharacter.updatedAt;
    }
  } else {
    normalized.updatedAt = Date.now();
  }

  return normalized;
};

/**
 * Normalize family data from backend to frontend format
 */
const normalizeFamily = (backendFamily) => {
  if (!backendFamily) return null;

  const normalized = {
    ...backendFamily,
    id: backendFamily.id || backendFamily._id?.toString() || backendFamily._id,
    projectId: backendFamily.projectId?.toString() || backendFamily.projectId,
    members: (backendFamily.members || []).map((member) =>
      typeof member === 'object' && member._id
        ? member._id.toString()
        : member?.toString() || member
    ),
  };

  // Convert Date strings to epoch timestamps
  if (backendFamily.createdAt) {
    if (typeof backendFamily.createdAt === 'string') {
      normalized.createdAt = new Date(backendFamily.createdAt).getTime();
    } else if (backendFamily.createdAt instanceof Date) {
      normalized.createdAt = backendFamily.createdAt.getTime();
    } else {
      normalized.createdAt = backendFamily.createdAt;
    }
  } else {
    normalized.createdAt = Date.now();
  }

  if (backendFamily.updatedAt) {
    if (typeof backendFamily.updatedAt === 'string') {
      normalized.updatedAt = new Date(backendFamily.updatedAt).getTime();
    } else if (backendFamily.updatedAt instanceof Date) {
      normalized.updatedAt = backendFamily.updatedAt.getTime();
    } else {
      normalized.updatedAt = backendFamily.updatedAt;
    }
  } else {
    normalized.updatedAt = Date.now();
  }

  return normalized;
};

const CharactersEditorPage = () => {
  const { projectId } = useParams();
  const { colors } = useThemeStyles();
  const dispatch = useDispatch();

  const activeCharacterId = useSelector(selectActiveCharacterId);
  const activeCharacter = useSelector(selectCharacterById(activeCharacterId));

  const [addCharacterModalOpen, setAddCharacterModalOpen] = useState(false);
  const [familyTreeModalOpen, setFamilyTreeModalOpen] = useState(false);
  const [selectedFamilyId, setSelectedFamilyId] = useState(null);
  const [centerOnCharacterId, setCenterOnCharacterId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load characters and families from backend on mount
  useEffect(() => {
    const loadData = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Load characters and families in parallel
        const [charactersResponse, familiesResponse] = await Promise.all([
          characterService.getCharacters(projectId),
          characterService.getFamilies(projectId),
        ]);

        if (charactersResponse.error) {
          console.error('Error loading characters:', charactersResponse.error);
        } else if (charactersResponse.data?.data?.characters) {
          const normalizedCharacters =
            charactersResponse.data.data.characters.map(normalizeCharacter);
          dispatch(setCharacters(normalizedCharacters));
        }

        if (familiesResponse.error) {
          console.error('Error loading families:', familiesResponse.error);
        } else if (familiesResponse.data?.data?.families) {
          const normalizedFamilies = familiesResponse.data.data.families.map(normalizeFamily);
          dispatch(setFamilies(normalizedFamilies));
        }
      } catch (err) {
        console.error('Error loading character data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId, dispatch]);

  const handleCharacterClick = (character) => {
    dispatch(setActiveCharacter(character.id));
  };

  const handleCharacterClose = () => {
    dispatch(setActiveCharacter(null));
  };

  const handleFamilyClick = (family, characterId) => {
    if (family && family.id) {
      setSelectedFamilyId(family.id);
      setCenterOnCharacterId(characterId); // Center on the character that was clicked
      setFamilyTreeModalOpen(true);
    }
  };

  const handleFamilyTreeClose = () => {
    setFamilyTreeModalOpen(false);
    setSelectedFamilyId(null);
    setCenterOnCharacterId(null);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          backgroundColor: colors.background.primary,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        backgroundColor: colors.background.primary,
        position: 'relative',
      }}
    >
      {/* Main List View */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          marginRight: activeCharacter ? '400px' : 0,
          transition: 'margin-right 0.3s ease',
        }}
      >
        <CharactersListPage
          projectId={projectId}
          onCharacterClick={handleCharacterClick}
          onFamilyClick={handleFamilyClick}
          onAddCharacter={() => setAddCharacterModalOpen(true)}
        />
      </Box>

      {/* Character Details Panel (Side Panel) */}
      {activeCharacter && (
        <CharacterDetailsPanel
          character={activeCharacter}
          onClose={handleCharacterClose}
          onDelete={handleCharacterClose}
        />
      )}

      {/* Add Character Modal */}
      <AddCharacterModal
        open={addCharacterModalOpen}
        onClose={() => setAddCharacterModalOpen(false)}
        projectId={projectId}
      />

      {/* Family Tree Modal */}
      <FamilyTreeModal
        open={familyTreeModalOpen}
        onClose={handleFamilyTreeClose}
        familyId={selectedFamilyId}
        centerOnCharacterId={centerOnCharacterId}
        projectId={projectId}
      />
    </Box>
  );
};

export default CharactersEditorPage;
